import { Link } from '@tanstack/react-router';
import { CheckCircle2, ChevronRight, PackageX, Zap } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { CatalogOperationsLayout } from '~/features/catalog-operations/components/catalog-operations-layout';
import { ProductsListView } from '~/features/catalog-operations/components/products-list-view';
import { DEFAULT_PAGE_SIZE } from '~/features/catalog-operations/constants/products-list';
import {
  useCurationFilters,
  useGlobalNeedsAttentionProducts,
} from '~/features/catalog-operations/hooks/use-batch-detail';
import type { ProductFilterValues } from '~/features/catalog-operations/types/catalog-operations.types';
import { useSubsidiaryProducts } from '~/features/platform/hooks/use-subsidiary-products';
import { useAuthStore } from '~/store/auth.store';
import { PLATFORM_PRODUCT_CODES } from '~/store/trial.store';

/**
 * Standalone list of products flagged as needing attention across all batches.
 * Cross-connector mode: both the listing and the filter-definitions calls
 * omit `connector_code`, so the drawer surfaces only the overlap filter set
 * (title + source_product_id).
 */

export function NeedsAttentionProductsPage() {
  const [appliedFilters, setAppliedFilters] = useState<ProductFilterValues>({});
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const resetPagination = useCallback(() => {
    setPage(1);
  }, []);

  function handleFiltersChange(filters: ProductFilterValues) {
    setAppliedFilters(filters);
    resetPagination();
  }

  function handleSearchSubmit(value: string) {
    setSearch(value);
    resetPagination();
  }

  function handleViewModeChange(mode: 'card' | 'table') {
    setViewMode(mode);
    // Page size carries across the view switch; both modes share one scale.
    resetPagination();
  }

  // Needs-attention data is production-only — scope the call to whichever
  // single platform is LIVE-enrolled so the backend doesn't try to overlay
  // a platform that isn't actually delivering. Cross-connector mode (no
  // connector_code) only makes sense when both platforms are live.
  const subsidiaryId = useAuthStore((s) => s.user?.activeTenantId);
  const subsidiaryName = useAuthStore(
    (s) => s.user?.accessibleTenants.find((tenant) => tenant.id === subsidiaryId)?.name ?? '',
  );
  const { data: enrolledProducts } = useSubsidiaryProducts(subsidiaryId);
  const enrollmentResolved = enrolledProducts !== undefined;
  const googleContract =
    enrolledProducts?.find((p) => p.productCode === PLATFORM_PRODUCT_CODES.google)?.contractType ??
    null;
  const metaContract =
    enrolledProducts?.find((p) => p.productCode === PLATFORM_PRODUCT_CODES.meta)?.contractType ??
    null;
  const isGoogleLive = googleContract === 'LIVE';
  const isMetaLive = metaContract === 'LIVE';
  const noneLive = enrollmentResolved && !isGoogleLive && !isMetaLive;
  const connectorCode =
    isGoogleLive && !isMetaLive
      ? 'GOOGLE_MERCHANT'
      : isMetaLive && !isGoogleLive
        ? 'META_COMMERCE'
        : undefined;

  const {
    data,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
  } = useGlobalNeedsAttentionProducts({
    ...(connectorCode ? { connectorCode } : {}),
    page,
    size: pageSize,
    filters: appliedFilters,
    search,
    enabled: enrollmentResolved && !noneLive,
  });
  // No connector code on this page — overlap filter set only.
  const { data: filterDefinitions } = useCurationFilters(undefined);

  const products = data?.products ?? [];
  const total = data?.total ?? 0;
  const isLoading = !enrollmentResolved || isProductsLoading;
  const hasFiltersApplied = Object.keys(appliedFilters).length > 0 || search.length > 0;
  // Show the celebratory empty state only when *nothing* is flagged. When
  // filters/search are active and matched zero rows, the list view shows its
  // own "No products found." so the user knows their query didn't match.
  const showEmptyState = !isLoading && total === 0 && !hasFiltersApplied;

  return (
    <CatalogOperationsLayout testId="needs-attention-page">
      {/* No max-width: this is a filter rail beside a product grid, and capping it
          at 7xl left both cramped while the viewport had space to give. Matches
          the batch products page, which is the same layout. */}
      <div className="mx-auto flex h-full min-h-0 flex-col gap-4">
        <nav data-testid="needs-attention-breadcrumb" className="flex items-center gap-1.5 text-sm">
          <Link
            data-testid="needs-attention-breadcrumb-catalog-ops-link"
            to="/catalog-operations"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors"
          >
            <Zap className="h-4 w-4 text-amber-500" aria-hidden="true" />
            <span>Catalog Operations</span>
          </Link>
          <ChevronRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
          <span data-testid="needs-attention-title" className="font-semibold">
            Products Needing Attention
          </span>
        </nav>

        {!enrollmentResolved ? (
          <Skeleton data-testid="needs-attention-loading" className="h-64 w-full rounded-xl" />
        ) : noneLive ? (
          <NotEnrolledPlaceholder subsidiaryName={subsidiaryName} />
        ) : showEmptyState ? (
          <NoNeedsAttentionPlaceholder />
        ) : (
          <ProductsListView
            columnsStorageKey="needs-attention-products"
            title="Products Needing Attention"
            products={products}
            total={total}
            isLoading={isLoading}
            isFetching={isProductsFetching}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            productLinkContext="needs-attention"
            filterDefinitions={filterDefinitions ?? []}
            appliedFilters={appliedFilters}
            onFiltersChange={handleFiltersChange}
            search={search}
            onSearchSubmit={handleSearchSubmit}
          />
        )}
      </div>
    </CatalogOperationsLayout>
  );
}

/** Shown when neither Google Ads nor Meta Ads has a LIVE contract — needs-attention
 *  data is production-only, so there's nothing to fetch for a trial-only or
 *  fully unenrolled tenant. */
function NotEnrolledPlaceholder({ subsidiaryName }: { subsidiaryName: string }) {
  const { t } = useTranslation('catalog-operations');
  return (
    <div
      data-testid="needs-attention-not-enrolled"
      className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-white px-6 py-16 text-center"
    >
      <PackageX className="text-muted-foreground h-12 w-12" aria-hidden="true" />
      <h3 className="text-base font-semibold">{t('notEnrolled.title')}</h3>
      <p className="text-muted-foreground max-w-md text-sm">
        {t('notEnrolled.description', { subsidiaryName })}
      </p>
      <Button
        asChild
        data-testid="needs-attention-contact-sales-button"
        variant="default"
        className="cursor-pointer"
      >
        <a data-testid="needs-attention-contact-sales-button" href="mailto:sales@lily.ai">
          {t('notEnrolled.cta')}
        </a>
      </Button>
    </div>
  );
}

/** Empty-state shown when no cross-batch products are flagged for attention. */
function NoNeedsAttentionPlaceholder() {
  return (
    <div
      data-testid="needs-attention-empty"
      className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-white px-6 py-16 text-center"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
        <CheckCircle2 className="h-6 w-6 text-green-600" aria-hidden="true" />
      </div>
      <h3 className="text-base font-semibold">No products need attention right now.</h3>
      <p className="text-muted-foreground max-w-md text-sm">
        Flagged products across all batches will appear here when they require review.
      </p>
    </div>
  );
}
